import * as React from 'react';
import { Upload, ImagePlus, AlertCircle } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { OverlayCloseButton } from '@ooh/ui/components/overlay-close-button';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';

import {
  buildGalleryPhotos,
  reorderGalleryPhotos,
  type PhotoGalleryItem,
  type PhotoUploadSectionProps,
} from './lib/photo-gallery';

export function PhotoUploadSection(props: PhotoUploadSectionProps) {
  const { t, fileInputRef, onFileChange, photoCountError } = props;
  const galleryPhotos = React.useMemo(() => buildGalleryPhotos(props), [props]);
  const [draggedId, setDraggedId] = React.useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = React.useState<string | null>(null);
  const [previewPhotos, setPreviewPhotos] = React.useState<PhotoGalleryItem[]>(galleryPhotos);

  React.useEffect(() => {
    if (!draggedId) {
      setPreviewPhotos(galleryPhotos);
    }
  }, [draggedId, galleryPhotos]);

  const subtitle =
    props.mode === 'create'
      ? t('form.sections.photosSubtitle')
      : t('form.sections.photosEditSubtitle');

  const dropzoneText =
    props.mode === 'create' ? t('form.photos.dropzone') : t('form.photos.dropzoneEdit');

  const hintText = props.mode === 'create' ? t('form.photos.hint') : t('form.photos.hintEdit');
  const counterText = t('form.photos.counter', {
    defaultValue: `${previewPhotos.length}/10 zdjęć, JPG/PNG/WebP do 10 MB`,
  });
  const emptyText = t('form.photos.emptyState', {
    defaultValue: 'Brak zdjęć. Dodaj minimum jedno zdjęcie frontu nośnika.',
  });
  const reorderTitle = t('form.photos.reorderTitle', {
    defaultValue: 'Kolejność zdjęć w galerii',
  });
  const reorderHint = t('form.photos.reorderHint', {
    defaultValue: 'Przeciągnij zdjęcie, aby zmienić kolejność',
  });
  const newLabel = t('form.photos.newLabel', { defaultValue: 'Nowe' });

  const handleDropzoneClick = () => {
    props.onAddPhoto?.();
    if (!props.onAddPhoto) {
      fileInputRef.current?.click();
    }
  };

  const handleDropzoneDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const { files } = event.dataTransfer;
    if (!files.length) return;
    onFileChange(files);
  };

  const handleDragOver = (targetId: string) => {
    if (!draggedId || draggedId === targetId) {
      return;
    }

    setDropTargetId(targetId);
    setPreviewPhotos((prev) => reorderGalleryPhotos(prev, draggedId, targetId));
  };

  const handleDrop = (targetId: string) => {
    if (draggedId && draggedId !== targetId) {
      props.onReorderPhotos?.(draggedId, targetId);
    }

    setDraggedId(null);
    setDropTargetId(null);
  };

  return (
    <Card data-testid="section-photos">
      <CardHeader className="pb-4">
        <CardTitle className="text-[15px]">{t('form.sections.photos')}</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </CardHeader>
      <CardContent>
        <div data-testid="photo-upload-area">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 mb-4">
            <div className="text-xs text-muted-foreground">{counterText}</div>
          </div>

          {photoCountError ? (
            <p
              className="text-2xs text-destructive flex items-center gap-1 mb-2"
              data-testid="photo-count-error"
            >
              <AlertCircle className="h-3 w-3 shrink-0" />
              {photoCountError}
            </p>
          ) : null}

          {/* Drop zone */}
          <div
            className="border-2 border-dashed border-border/60 rounded-lg p-6 text-center hover:border-primary/40 transition-colors cursor-pointer"
            onClick={handleDropzoneClick}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDropzoneDrop}
            data-testid="photo-dropzone"
          >
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <ImagePlus className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-[13px] font-medium text-foreground">{dropzoneText}</p>
                <p className="text-2xs text-muted-foreground mt-0.5">{hintText}</p>
              </div>
              <div className="mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid={props.mode === 'create' ? 'upload-button' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  {props.mode === 'create' && <Upload className="h-3.5 w-3.5" />}
                  {t('form.photos.selectFiles')}
                </Button>
              </div>
            </div>
          </div>

          <Input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => {
              const { files } = e.target;
              if (!files) return;
              onFileChange(files);
            }}
            data-testid={props.mode === 'edit' ? 'photo-file-input' : undefined}
          />

          {/* Photo previews */}
          {previewPhotos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-4 py-5 text-center text-xs text-muted-foreground mt-4">
              {emptyText}
            </div>
          ) : (
            <div className="space-y-2 mt-4">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-xs font-semibold text-foreground">{reorderTitle}</h4>
                <span className="text-2xs text-muted-foreground">{reorderHint}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="photo-previews">
                {previewPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    draggable={Boolean(props.onReorderPhotos)}
                    onDragStart={() => setDraggedId(photo.id)}
                    onDragEnd={() => {
                      setDraggedId(null);
                      setDropTargetId(null);
                    }}
                    onDragOver={(event) => {
                      if (!props.onReorderPhotos) {
                        return;
                      }

                      event.preventDefault();
                      handleDragOver(photo.id);
                    }}
                    onDrop={(event) => {
                      if (!props.onReorderPhotos) {
                        return;
                      }

                      event.preventDefault();
                      handleDrop(photo.id);
                    }}
                    className={`group relative rounded-lg overflow-hidden border transition-all duration-150 ${photo.isNew ? 'border-primary/40' : 'border-border/50'} ${draggedId === photo.id ? 'opacity-35 scale-95 ring-2 ring-primary' : ''} ${dropTargetId === photo.id && draggedId !== photo.id ? 'ring-2 ring-primary border-primary bg-primary/5 shadow-md' : ''}`}
                    data-testid={photo.tileTestId}
                  >
                    <img
                      src={photo.url}
                      alt={photo.name}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <OverlayCloseButton
                      onClick={photo.onRemove}
                      aria-label={t('form.photos.removeAriaLabel', { name: photo.name })}
                      data-testid={photo.removeTestId}
                    />
                    {photo.isNew ? (
                      <div className="absolute left-2 top-2 rounded-full bg-background/90 px-2 py-0.5 text-2xs font-medium text-foreground shadow-sm">
                        {newLabel}
                      </div>
                    ) : null}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                      <p className="text-2xs text-white truncate">{photo.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
